import { useIsMobile } from "../../hooks/useMobile";
import { ReactElement, useMemo } from "react";
import { SidebarProvider, SidebarTrigger } from "../../components/ui/sidebar";
import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import { useViewSurveyData } from "../view-survey/useViewSurveyData";
import { SurveyResultsSidebar } from "./survey-results-sidebar";
import ResultsQuestionList from "../view-survey/components/results-question-list.component";
import { combineQuestionStats } from "../view-survey/view-survey.helpers";
import SurveySummary from "./survey-results-summary.component";
import { SurveyCompleteDialog, useSurveyCompleteDialogSetup } from "../view-survey/components/survey-complete-dialog.component";
import { useSurveySummary } from "../view-survey/survey-action-plan/useSurveySummary";
import { useSurveyMediaPlan } from "../view-survey/survey-action-plan/useSurveyMediaPlan";
import { useGetPulsePollingResponsesQuery } from "../../redux/survey/survey.endpoints";

const SurveyResultsPage = (): ReactElement => {
  useSetPageTitle('Survey results');
  const { isShare, surveyId, stats, survey, summary, showSummaries } = useViewSurveyData();
  const { isMobile } = useIsMobile();
  const { showDialog, setShowDialog } = useSurveyCompleteDialogSetup({
    mode: isShare ? 'share' : 'view',
    surveyId: survey?.extSurveyId,
    status: survey?.surveyStatus,
  });

  const { data: pulsePollingData } = useGetPulsePollingResponsesQuery(
    { ext_survey_id: surveyId },
    { skip: !surveyId }
  );

  const questions = useMemo(() => {
    return combineQuestionStats(stats?.questions, survey?.questions, showSummaries ? summary?.questions : undefined);
  }, [stats, survey, summary]);

  const { isLoadingSummary, isGeneratingSummary, createSummary } = useSurveySummary(surveyId, survey?.surveyStatus, isShare);
  const { isLoadingPlan, isGeneratingMediaPlan, createMediaPlan } = useSurveyMediaPlan(surveyId, survey?.surveyStatus, isShare);

  return (
    <SidebarProvider>
      <SurveyCompleteDialog
        surveyId={surveyId}
        showDialog={showDialog}
        setShowDialog={setShowDialog}
      />
      <SurveyResultsSidebar
        surveyId={surveyId}
        isShare={isShare}
        createSummary={createSummary}
        createMediaPlan={createMediaPlan}
      />
      <div className={`w-full p-4 md:p-10 ${showDialog ? 'max-h-screen overflow-y-hidden' : ''}`}>
        {isMobile && (
          <div className="flex size-12 items-center justify-center">
            <SidebarTrigger />
          </div>
        )}
        <SurveySummary
          surveyId={surveyId}
          isShare={isShare}
          isLoadingSummary={isLoadingSummary}
          isGeneratingSummary={isGeneratingSummary}
          createSummary={createSummary}
          isLoadingMediaPlan={isLoadingPlan}
          isGeneratingMediaPlan={isGeneratingMediaPlan}
          createMediaPlan={createMediaPlan}
        />
        <ResultsQuestionList
          questions={questions}
          aiMaxCompletes={survey?.maxAICompletes ?? undefined}
          humanMaxCompletes={survey?.maxCompletes ?? undefined}
          surveyId={surveyId}
          audienceId={survey?.audience ?? ''}
          isShare={isShare}
          pulsePollingData={pulsePollingData ?? undefined}
        />
      </div>
    </SidebarProvider>
  );
};

export default SurveyResultsPage;
